import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Video, HelpCircle, TrendingUp, Clock } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import * as educationService from '../services/educationService';
import type { EducationCategory, EducationArticle } from '../services/educationService';
import { getAbsoluteImageUrl } from '../services/educationService';

export default function EducationPage() {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<EducationCategory[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<EducationArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [cats, articles] = await Promise.all([
        educationService.getCategories(),
        educationService.getFeaturedArticles()
      ]);
      
      setCategories(cats);
      setFeaturedArticles(articles.slice(0, 3));
    } catch (error) {
      console.error('Error loading education data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Se încarcă...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      <PageHeader 
        title="Educație & Resurse"
        subtitle="Învață despre sănătate, nutriție și wellness"
        showBack={true}
        backTo="/dashboard"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-8 text-white">
          <div className="max-w-3xl">
            <h2 className="text-3xl font-bold mb-4">
              Descoperă resurse pentru o viață mai sănătoasă
            </h2>
            <p className="text-purple-100 text-lg mb-6">
              Articole, ghiduri video și răspunsuri la întrebările tale despre sănătate, 
              scrise de specialiști și adaptate nevoilor tale.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/education/articles')}
                className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors"
              >
                <BookOpen className="w-5 h-5 inline mr-2" />
                Explorează Articole
              </button>
              <button
                onClick={() => navigate('/education/faqs')}
                className="px-6 py-3 bg-purple-500 text-white rounded-lg font-semibold hover:bg-purple-400 transition-colors"
              >
                <HelpCircle className="w-5 h-5 inline mr-2" />
                Întrebări Frecvente
              </button>
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Explorează pe Categorii
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => navigate(`/education/articles?category=${category.id}`)}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md hover:border-purple-300 transition-all text-left group"
              >
                <div className="flex items-start gap-4">
                  {category.icon && (
                    <span className="text-4xl">{category.icon}</span>
                  )}
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors mb-2">
                      {category.name}
                    </h3>
                    {category.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">
                        {category.description}
                      </p>
                    )}
                    <div className="mt-3 flex items-center text-sm text-gray-500">
                      <BookOpen className="w-4 h-4 mr-1" />
                      {category._count?.articles || 0} articole
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Featured Articles */}
        {featuredArticles.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">
                Articole Recomandate
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <button
                  key={article.id}
                  onClick={() => navigate(`/education/article/${article.slug}`)}
                  className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-200 hover:shadow-md transition-all text-left group"
                >
                  {article.imageUrl && (
                    <div className="aspect-video bg-gray-100 overflow-hidden">
                      <img
                        src={getAbsoluteImageUrl(article.imageUrl)}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      {article.type === 'VIDEO' ? (
                        <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium flex items-center gap-1">
                          <Video className="w-3 h-3" />
                          Video
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          Articol
                        </span>
                      )}
                      {article.category && (
                        <span className="text-xs text-gray-500">
                          {article.category.icon} {article.category.name}
                        </span>
                      )}
                    </div>
                    
                    <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors mb-2 line-clamp-2">
                      {article.title}
                    </h3>
                    
                    {article.excerpt && (
                      <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                        {article.excerpt}
                      </p>
                    )}
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      {article.readTime && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {article.readTime} min
                        </div>
                      )}
                      <div>
                        {article.viewCount} vizualizări
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="bg-white rounded-xl p-8 border border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Acces Rapid
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <button
              onClick={() => navigate('/education/articles')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-colors"
            >
              <BookOpen className="w-8 h-8 text-purple-600" />
              <div className="text-left">
                <div className="font-semibold text-gray-900">Toate Articolele</div>
                <div className="text-sm text-gray-600">Vezi biblioteca completă</div>
              </div>
            </button>
            
            <button
              onClick={() => navigate('/education/faqs')}
              className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-yellow-300 hover:bg-yellow-50 transition-colors"
            >
              <HelpCircle className="w-8 h-8 text-yellow-600" />
              <div className="text-left">
                <div className="font-semibold text-gray-900">Întrebări & Răspunsuri</div>
                <div className="text-sm text-gray-600">FAQ complet</div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
